import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { portfolioProjectService } from '../services/portfolioProjectService';
import './Email.css';

const PortfolioProjectForm = ({ show, onHide, projectId, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    category: 'Commercial',
    year: new Date().getFullYear().toString(),
    imageAlt: '',
    status: 'completed',
    featured: false,
    displayOrder: 0,
    slug: '',
    clientName: '',
    projectSize: '',
    startDate: '',
    completionDate: '',
    tags: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingImage, setExistingImage] = useState(null);

  useEffect(() => {
    if (show) {
      if (projectId) {
        loadProject();
      } else {
        // Reset form for new project
        setFormData({
          name: '',
          description: '',
          location: '',
          category: 'Commercial',
          year: new Date().getFullYear().toString(),
          imageAlt: '',
          status: 'completed',
          featured: false,
          displayOrder: 0,
          slug: '',
          clientName: '',
          projectSize: '',
          startDate: '',
          completionDate: '',
          tags: ''
        });
        setImageFile(null);
        setImagePreview(null);
        setExistingImage(null);
        setError(null);
      }
    }
  }, [show, projectId]);

  const loadProject = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await portfolioProjectService.getPortfolioProjectById(projectId);
      if (response.success && response.data) {
        const project = response.data;
        setFormData({
          name: project.name || '',
          description: project.description || '',
          location: project.location || '',
          category: project.category || 'Commercial',
          year: project.year || new Date().getFullYear().toString(),
          imageAlt: project.imageAlt || '',
          status: project.status || 'completed',
          featured: project.featured || false,
          displayOrder: project.displayOrder || 0,
          slug: project.slug || '',
          clientName: project.clientName || '',
          projectSize: project.projectSize || '',
          startDate: project.startDate ? project.startDate.split('T')[0] : '',
          completionDate: project.completionDate ? project.completionDate.split('T')[0] : '',
          tags: Array.isArray(project.tags) ? project.tags.join(', ') : (project.tags || '')
        });
        if (project.image) {
          setExistingImage(project.image);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setExistingImage(null); // Clear existing image when new one is selected
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.name || !formData.description || !formData.location || !formData.category || !formData.year) {
      setError('Please fill in all required fields');
      return;
    }

    // For new projects, image is required
    if (!projectId && !imageFile) {
      setError('Image is required for new projects');
      return;
    }

    try {
      setLoading(true);

      // Create FormData for file upload
      const submitFormData = new FormData();
      submitFormData.append('name', formData.name);
      submitFormData.append('description', formData.description);
      submitFormData.append('location', formData.location);
      submitFormData.append('category', formData.category);
      submitFormData.append('year', formData.year);
      submitFormData.append('imageAlt', formData.imageAlt);
      submitFormData.append('status', formData.status);
      submitFormData.append('featured', formData.featured);
      submitFormData.append('displayOrder', formData.displayOrder);
      if (formData.slug) submitFormData.append('slug', formData.slug);
      if (formData.clientName) submitFormData.append('clientName', formData.clientName);
      if (formData.projectSize) submitFormData.append('projectSize', formData.projectSize);
      if (formData.startDate) submitFormData.append('startDate', formData.startDate);
      if (formData.completionDate) submitFormData.append('completionDate', formData.completionDate);
      if (formData.tags) {
        // Convert comma-separated tags to array
        const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
        submitFormData.append('tags', JSON.stringify(tagsArray));
      }

      // Add image file if provided
      if (imageFile) {
        submitFormData.append('image', imageFile);
      }

      let response;
      if (projectId) {
        response = await portfolioProjectService.updatePortfolioProject(projectId, submitFormData);
      } else {
        response = await portfolioProjectService.createPortfolioProject(submitFormData);
      }

      if (response.success) {
        if (onSuccess) {
          onSuccess();
        }
        onHide();
      }
    } catch (err) {
      setError(err.message || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{projectId ? 'Edit Portfolio Project' : 'Create Portfolio Project'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category *</Form.Label>
                <Form.Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Commercial">Commercial</option>
                  <option value="Industrial">Industrial</option>
                  <option value="Residential">Residential</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Location *</Form.Label>
                <Form.Control
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Year *</Form.Label>
                <Form.Control
                  type="text"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Image {!projectId && '*'}</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              required={!projectId}
            />
            {(imagePreview || existingImage) && (
              <div className="mt-2">
                <img
                  src={imagePreview || existingImage}
                  alt="Preview"
                  style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', borderRadius: '4px' }}
                />
              </div>
            )}
            <Form.Text className="text-muted">
              {projectId ? 'Leave empty to keep existing image' : 'Required for new projects'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Image Alt Text</Form.Label>
            <Form.Control
              type="text"
              name="imageAlt"
              value={formData.imageAlt}
              onChange={handleInputChange}
              placeholder="Alternative text for the image"
            />
          </Form.Group>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="completed">Completed</option>
                  <option value="in-progress">In Progress</option>
                  <option value="upcoming">Upcoming</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Display Order</Form.Label>
                <Form.Control
                  type="number"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="featured"
                  label="Featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Client Name</Form.Label>
                <Form.Control
                  type="text"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Project Size</Form.Label>
                <Form.Control
                  type="text"
                  name="projectSize"
                  value={formData.projectSize}
                  onChange={handleInputChange}
                  placeholder="e.g., 50,000 sq ft"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Completion Date</Form.Label>
                <Form.Control
                  type="date"
                  name="completionDate"
                  value={formData.completionDate}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Tags</Form.Label>
            <Form.Control
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="Comma-separated tags (e.g., office, commercial, dallas)"
            />
            <Form.Text className="text-muted">
              Separate multiple tags with commas
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Slug</Form.Label>
            <Form.Control
              type="text"
              name="slug"
              value={formData.slug}
              onChange={handleInputChange}
              placeholder="URL-friendly identifier (auto-generated if empty)"
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Saving...' : (projectId ? 'Update' : 'Create')}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default PortfolioProjectForm;

